import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format, addDays } from "date-fns";
import {
  Ship,
  Car,
  Bed,
  Sparkles,
  ArrowRight,
  Check,
  Clock,
  Loader2,
  Users,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getAllFerries } from "@/services/ferryService";
import { getAllTransport } from "@/services/transportService";
import { getAllAccommodation } from "@/services/accommodationService";
import AccommodationOptionsDialog from "./AccommodationOptionsDialog";

import { slotsForDate, getTimeSlots } from "@/utils/ferryWeekdayUtils";
import {
  isVehicleFreeForBooking,
  calculateAccommodationTotal,
  calculateFerryTotal,
  calculateTransportTotal,
  deriveTeePeriod,
  defaultRoomsForPlayers,
  minRoomsForPlayers,
  formatSGD,
  SHARING_TYPES,
} from "@/utils/pricingEngine";

function sortedByPrice(list, priceKey) {
  return list.slice().sort((a, b) => (a[priceKey] || 0) - (b[priceKey] || 0));
}

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function earliestTimeAfter(times, afterTime) {
  if (!times?.length) return null;
  const afterMin = timeToMinutes(afterTime);
  if (afterMin == null) return times[0];
  const eligible = times.filter((t) => timeToMinutes(t) >= afterMin);
  return eligible.length ? eligible[0] : times[times.length - 1];
}

function addMinutesToTime(t, minutes) {
  const base = timeToMinutes(t);
  if (base == null) return null;
  const DAY = 24 * 60;
  const total = (((base + minutes) % DAY) + DAY) % DAY; // wraps correctly for negative offsets too
  const h = String(Math.floor(total / 60)).padStart(2, "0");
  const m = String(total % 60).padStart(2, "0");
  return `${h}:${m}`;
}

const ROUND_DURATION_MIN = 270; // ~4.5 hours for 18 holes — used to estimate when the final round wraps up
const HOTEL_CHECK_IN_TIME = "14:00"; // standard, fixed hotel check-in time

// ── Time zones ──────────────────────────────────────────────────────────────
// Batam (ID, WIB) is 1 hour BEHIND Singapore (SG, SGT).
// - Inbound ferry departure times are Singapore clock (SG Time).
// - Ferry arrival, transport pickups, tee times, outbound ferry departure are
//   Batam clock (ID Time). Outbound ferry arrival is Singapore clock (SG Time).
// Example: depart 08:20 SG, 60 min crossing → arrive 09:20 SG = 08:20 ID.
const SGT_TO_WIB_OFFSET_MIN = -60;
const DEFAULT_FERRY_DURATION_MIN = 60; // real ferry docs don't store a duration
const INBOUND_PICKUP_BUFFER_MIN = 30; // inbound pickup = ferry arrival (ID Time) + 30 min
const OUTBOUND_PICKUP_BUFFER_MIN = 90; // outbound pickup = ferry departure (ID Time) - 90 min
// Ferry must land at least this long before the tee time: pickup plus up to
// 30 min drive to the furthest course, with time left to register.
const MIN_ARRIVAL_BEFORE_TEE_MIN = 60;

// Display helpers — every time shown to the golfer carries its time zone
// label, in 24h "0820" form.
function hhmm(t) {
  return t ? t.replace(":", "") : "";
}
function idTime(t) {
  return t ? `${hhmm(t)} ID Time` : "—";
}
function sgTime(t) {
  return t ? `${hhmm(t)} SG Time` : "—";
}
// "09 Oct 2026"
function fmtDate(d) {
  return d ? format(d, "dd MMM yyyy") : "";
}

const HOTEL_CHECK_OUT_TIME = "12:00"; // standard, fixed hotel check-out time
const TIME_ZONE_NOTE = "Batam Time (ID) is one hour behind Singapore Time (SG)";

// Inbound ferry arrival on the Batam (ID) clock
function ferryArrivalWib(ferry, depSgt) {
  if (!ferry || !depSgt) return null;
  const duration = ferry.duration_minutes || DEFAULT_FERRY_DURATION_MIN;
  return addMinutesToTime(depSgt, duration + SGT_TO_WIB_OFFSET_MIN);
}

// Outbound ferry arrival on the Singapore (SG) clock.
// Departs Batam (ID Time), so add the crossing and the 1h time difference.
// e.g. 15:20 ID + 60 min + 60 min = 17:20 SG.
function ferryOutboundArrivalSgt(ferry, depWib) {
  if (!ferry || !depWib) return null;
  const duration = ferry.duration_minutes || DEFAULT_FERRY_DURATION_MIN;
  return addMinutesToTime(depWib, duration - SGT_TO_WIB_OFFSET_MIN);
}

// Inbound pickup (ID Time) = ferry arrival (ID Time) + 30 min.
function earliestInboundPickupWib(ferry, depSgt) {
  const arrival = ferryArrivalWib(ferry, depSgt);
  if (!arrival) return null;
  return addMinutesToTime(arrival, INBOUND_PICKUP_BUFFER_MIN);
}

// Latest sailing (SG departure) that lands in Batam at least
// MIN_ARRIVAL_BEFORE_TEE_MIN before the tee time (ID Time)
function latestFerryArrivingBefore(times, ferry, teeTimeWib) {
  if (!times?.length) return null;
  const teeMin = timeToMinutes(teeTimeWib);
  if (teeMin == null) return times[times.length - 1];
  const eligible = times.filter(
    (t) =>
      timeToMinutes(ferryArrivalWib(ferry, t)) <=
      teeMin - MIN_ARRIVAL_BEFORE_TEE_MIN,
  );
  return eligible.length ? eligible[eligible.length - 1] : null;
}

// Does this land transport serve the ferry's Batam terminal? Compared
// case-insensitively against either end of the vehicle's route.
function norm(s) {
  return (s || "").trim().toLowerCase();
}
function servesTerminal(vehicle, ferry) {
  if (!ferry) return false;
  const term = norm(ferry.destination);
  if (!term) return false;
  return norm(vehicle.originport) === term || norm(vehicle.destination) === term;
}

function TimeEditor({ label, value, options, onChange }) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#0B2421] mb-1.5">{label}</p>
      <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto">
        {options.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange(t)}
            className={`h-8 rounded-lg border text-xs font-medium transition-colors ${
              value === t
                ? "bg-[#0B2421] text-white border-[#0B2421]"
                : "bg-gray-50 text-gray-700 border-gray-200 hover:border-[#0B2421] hover:text-[#0B2421]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function TravelCategoryCard({
  icon: Icon,
  title,
  confirmMessage,
  selected,
  recommendedId,
  options,
  metaLines,
  note,
  getPriceLabel,
  getName,
  getSubtitle,
  onChange,
  timeEditors,
  allowChange = true,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!selected) return null;
  const AnyDialogContent = /** @type {any} */ (DialogContent);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      {selected.image && (
        <div className="relative h-36 overflow-hidden">
          <img
            src={selected.image}
            alt={getName(selected)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      )}
      <div className="p-5">
        {confirmMessage && (
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
            {confirmMessage}
          </p>
        )}
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 shrink-0">
            <Sparkles className="h-3 w-3 text-emerald-600" />
            <span className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
              {selected.id === recommendedId ? "Recommended" : "Selected"}
            </span>
          </div>
          {allowChange && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="text-xs font-semibold text-[#0B2421] bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-3 py-1 transition-colors shrink-0"
            >
              Change
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {title}
          </p>
        </div>
        <h3 className="text-[#0B2421] font-bold text-base mb-1">
          {getName(selected)}
        </h3>
        <p className="text-gray-500 text-xs mb-2">{getSubtitle(selected)}</p>
        {metaLines?.length > 0 && (
          <div className="mb-3 space-y-1">
            {metaLines.map((line, i) => (
              <p
                key={i}
                className={`text-xs flex items-start gap-1.5 font-medium ${line.warning ? "text-amber-600" : "text-gray-500"}`}
              >
                <span className="mt-0.5">{line.icon}</span>
                <span>{line.text}</span>
              </p>
            ))}
          </div>
        )}
        <p className="text-[#0B2421] font-bold text-lg">
          {getPriceLabel(selected)}
        </p>
        {note && (
          <p className="text-[11px] text-gray-400 italic mt-2 pt-2 border-t border-gray-100 leading-snug">
            {note}
          </p>
        )}
      </div>

      {allowChange && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <AnyDialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="text-[#0B2421]">
                Change {title}
              </DialogTitle>
              <DialogDescription>
                Pick a different option, or adjust the times below
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              {options.map((opt) => {
                const isCurrent = opt.id === selected.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => onChange(opt)}
                    className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-colors ${
                      isCurrent
                        ? "border-[#0B2421] bg-[#0B2421]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {opt.image && (
                      <img
                        src={opt.image}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[#0B2421] font-semibold text-sm truncate">
                        {getName(opt)}
                      </p>
                      <p className="text-gray-400 text-xs leading-snug break-words">
                        {getSubtitle(opt)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[#0B2421] font-bold text-sm whitespace-nowrap">
                        {getPriceLabel(opt)}
                      </span>
                      {isCurrent && (
                        <Check className="h-4 w-4 text-[#0B2421]" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {timeEditors?.length > 0 && (
              <div className="space-y-4 pt-4 border-t border-gray-100">
                {timeEditors.map((editor) => (
                  <TimeEditor key={editor.label} {...editor} />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="w-full mt-2 py-2.5 rounded-xl font-bold text-sm bg-[#0B2421] hover:bg-[#0B2421]/85 text-white transition-colors"
            >
              Done
            </button>
          </AnyDialogContent>
        </Dialog>
      )}
    </motion.div>
  );
}

/**
 * @param {{
 *   pkg: { title: string, nights: number },
 *   numPlayers: number,
 *   selectedCourses: any[],
 *   inboundDate: Date | undefined,
 *   inboundTeeTime: string | undefined,
 *   outboundDate: Date | undefined,
 *   outboundTeeTime: string | undefined,
 *   onSelectionChange: (selections: any) => void,
 *   onContinue: () => void,
 * }} props
 */
export default function RecommendedTravelStep({
  pkg,
  numPlayers,
  selectedCourses,
  inboundDate,
  inboundTeeTime,
  outboundDate,
  outboundTeeTime,
  onSelectionChange,
  onContinue,
}) {
  // Real ferry / land transport / accommodation docs, fetched once. Each
  // course links to a subset via ferry_route_ids / transport_vehicle_ids /
  // accommodation_hotel_ids — that subset drives the auto-recommendation,
  // but the "Change" popup shows the FULL list so the golfer can pick anything.
  const [ferries, setFerries] = useState([]);
  const [transports, setTransports] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [loadingTravelOptions, setLoadingTravelOptions] = useState(true);
  // Every room is a single room (king bed). Twin share is no longer offered —
  // instead the golfer picks how many rooms to book.
  const sharingType = SHARING_TYPES.SINGLE;
  useEffect(() => {
    (async () => {
      setLoadingTravelOptions(true);
      try {
        const [ferryList, transportList, accommodationList] = await Promise.all(
          [getAllFerries(), getAllTransport(), getAllAccommodation()],
        );
        setFerries(Array.isArray(ferryList) ? ferryList : []);
        setTransports(Array.isArray(transportList) ? transportList : []);
        setAccommodations(
          Array.isArray(accommodationList) ? accommodationList : [],
        );
      } catch (err) {
        console.error(
          "Failed to load ferry/transport/accommodation options:",
          err,
        );
        setFerries([]);
        setTransports([]);
        setAccommodations([]);
      } finally {
        setLoadingTravelOptions(false);
      }
    })();
  }, []);

  const [ferryOverride, setFerryOverride] = useState(null);
  const [ferryInboundTimeOverride, setFerryInboundTimeOverride] =
    useState(null);
  const [ferryOutboundTimeOverride, setFerryOutboundTimeOverride] =
    useState(null);
  const [accommodationOverride, setAccommodationOverride] = useState(null);
  const [accommodationDialogOpen, setAccommodationDialogOpen] = useState(false);

  const players = numPlayers || 1;
  const playersLabel = `${players} Player${players > 1 ? "s" : ""}`;
  // Rooms default to one per player; the golfer can lower it to share
  // (up to 3 per room). A player-count change resets it to the default.
  const minRooms = minRoomsForPlayers(players);
  const maxRooms = defaultRoomsForPlayers(players);
  const [numRoomsOverride, setNumRoomsOverride] = useState(null);
  useEffect(() => {
    setNumRoomsOverride(null);
  }, [players]);
  const numRooms = Math.min(
    maxRooms,
    Math.max(minRooms, numRoomsOverride ?? maxRooms),
  );
  const numRounds = selectedCourses?.length || 1; // one round = one course played
  const anchorCourse = selectedCourses[0]; // Round 1's course — the golfer's first stop after arrival.
  const lastCourse =
    selectedCourses[selectedCourses.length - 1] || anchorCourse; // last round's course — where the return journey starts.

  // Pricing plan #1: the vehicle is free (and hidden entirely) for a
  // single-round booking when the course is configured that way.
  const vehicleFree = isVehicleFreeForBooking(anchorCourse, numRounds);

  // Picking a different ferry route resets any time overrides — a new
  // route has its own timetable, so the old picked times no longer apply.
  // Land transport times are derived from the ferry times, so they follow
  // automatically.
  const handleFerryChange = (ferry) => {
    setFerryOverride(ferry);
    setFerryInboundTimeOverride(null);
    setFerryOutboundTimeOverride(null);
  };

  // Full, unfiltered lists — used as the "Change" popup's option set.
  const allFerryOptions = useMemo(
    () => sortedByPrice(ferries, "price"),
    [ferries],
  );
  const allAccommodationOptions = useMemo(
    () => sortedByPrice(accommodations, "price"),
    [accommodations],
  );

  // Rule: the AUTO-RECOMMENDED ferry is limited to routes the admin explicitly
  // linked to this course (via ferry_route_ids on the course doc).
  const ferryCandidates = useMemo(() => {
    const relevantIds = new Set(anchorCourse?.ferry_route_ids || []);
    const candidates = relevantIds.size
      ? ferries.filter((f) => relevantIds.has(f.id))
      : ferries;
    return sortedByPrice(candidates.length ? candidates : ferries, "price");
  }, [anchorCourse, ferries]);

  // Rule: the ferry must LAND in Batam (ID Time) 60+ min before the tee time. Departure
  // times are Singapore clock, so compare arrival (converted to ID Time), not
  // the raw departure time. Prefer the cheapest route with a valid sailing.
  const recommendedFerry = useMemo(() => {
    const feasible = ferryCandidates.find((f) => {
      const times = getTimeSlots(f, "departure").map((s) => s.time);
      return latestFerryArrivingBefore(times, f, inboundTeeTime);
    });
    return feasible || ferryCandidates[0] || null;
  }, [ferryCandidates, inboundTeeTime]);

  const selectedFerry = ferryOverride || recommendedFerry;

  const inboundFerryTimeOptions = useMemo(() => {
    if (!selectedFerry) return [];
    return slotsForDate(selectedFerry, inboundDate, "departure")
      .map((s) => s.time)
      .sort();
  }, [selectedFerry, inboundDate]);

  const selectedFerryTimeRaw = selectedFerry
    ? latestFerryArrivingBefore(
        inboundFerryTimeOptions,
        selectedFerry,
        inboundTeeTime,
      )
    : null;
  const ferryTimeConflict = !!(
    selectedFerry &&
    !selectedFerryTimeRaw &&
    !ferryInboundTimeOverride
  );
  // Inbound ferry departure — Singapore clock (SG Time)
  const selectedFerryTime =
    ferryInboundTimeOverride ||
    selectedFerryTimeRaw ||
    inboundFerryTimeOptions[0] ||
    null;

  // Rule: the AUTO-RECOMMENDED land transport is limited to options the admin
  // explicitly linked to this course (via transport_vehicle_ids), and prefers
  // vehicles that serve the terminal the ferry arrives at. The transfer card
  // is not user-changeable, so this is the vehicle that gets booked.
  const transportCandidates = useMemo(() => {
    const relevantIds = new Set(anchorCourse?.transport_vehicle_ids || []);
    const linked = relevantIds.size
      ? transports.filter((t) => relevantIds.has(t.id))
      : transports;
    const pool = sortedByPrice(linked.length ? linked : transports, "price");
    const atTerminal = pool.filter((t) => servesTerminal(t, selectedFerry));
    return atTerminal.length ? atTerminal : pool;
  }, [anchorCourse, transports, selectedFerry]);
  const recommendedTransport = transportCandidates[0] || null;
  const selectedTransport = recommendedTransport;

  // Pick-up location = the terminal the inbound ferry arrives at.
  const pickupTerminal = selectedFerry?.destination || null;

  // Inbound ferry arrival on the Batam (ID Time) clock.
  // e.g. 08:20 SG departure + 60 min = 09:20 SG = 08:20 ID.
  const selectedFerryArrivalTime = ferryArrivalWib(
    selectedFerry,
    selectedFerryTime,
  );

  // Rule: inbound pickup (ID Time) is exactly 30 minutes after the ferry
  // arrives (ID Time). e.g. arrives 08:20 ID → pickup 08:50 ID. Derived from
  // the ferry time, so it updates whenever the inbound ferry time changes.
  const selectedTransportTime = earliestInboundPickupWib(
    selectedFerry,
    selectedFerryTime,
  );

  // Outbound (return) ferry — departs Batam, so times are ID Time. Rule: must
  // depart late enough that a 2.5h pickup-and-travel buffer still starts
  // after the final round is estimated to finish.
  const estimatedFinishTime = outboundTeeTime
    ? addMinutesToTime(outboundTeeTime, ROUND_DURATION_MIN)
    : null;

  const outboundFerryTimeOptions = useMemo(() => {
    if (!selectedFerry) return [];
    const useReturn =
      selectedFerry.roundTrip && (selectedFerry.returnTimes || []).length > 0;
    return slotsForDate(
      selectedFerry,
      outboundDate,
      useReturn ? "return" : "departure",
    )
      .map((s) => s.time)
      .sort();
  }, [selectedFerry, outboundDate]);

  const earliestFeasibleFerryTime = estimatedFinishTime
    ? addMinutesToTime(estimatedFinishTime, 150)
    : null;
  const selectedOutboundFerryTimeRaw =
    selectedFerry && earliestFeasibleFerryTime
      ? earliestTimeAfter(outboundFerryTimeOptions, earliestFeasibleFerryTime)
      : null;
  const outboundFerryConflict = !!(
    selectedFerry &&
    earliestFeasibleFerryTime &&
    !selectedOutboundFerryTimeRaw &&
    !ferryOutboundTimeOverride
  );
  const selectedOutboundFerryTime =
    ferryOutboundTimeOverride ||
    selectedOutboundFerryTimeRaw ||
    outboundFerryTimeOptions[outboundFerryTimeOptions.length - 1] ||
    null;

  // Outbound ferry arrival in Singapore (SG Time).
  // e.g. 15:20 ID departure + 60 min + 1h time difference = 17:20 SG.
  const selectedOutboundFerryArrivalTime = ferryOutboundArrivalSgt(
    selectedFerry,
    selectedOutboundFerryTime,
  );

  // Outbound (return) transport — from the final round's course back to the
  // ferry terminal.
  const outboundTransportCandidates = useMemo(() => {
    const relevantIds = new Set(lastCourse?.transport_vehicle_ids || []);
    const linked = relevantIds.size
      ? transports.filter((t) => relevantIds.has(t.id))
      : transports;
    const pool = sortedByPrice(linked.length ? linked : transports, "price");
    const atTerminal = pool.filter((t) => servesTerminal(t, selectedFerry));
    return atTerminal.length ? atTerminal : pool;
  }, [lastCourse, transports, selectedFerry]);

  const recommendedOutboundTransport = outboundTransportCandidates[0] || null;

  // Rule: outbound pickup (ID Time) is exactly 90 minutes before the outbound
  // ferry departs (ID Time). Derived from the ferry time, so it updates
  // whenever the outbound ferry time changes.
  const selectedOutboundTransportTime = selectedOutboundFerryTime
    ? addMinutesToTime(selectedOutboundFerryTime, -OUTBOUND_PICKUP_BUFFER_MIN)
    : null;

  // Rule: the AUTO-RECOMMENDED hotel is tied directly to the first round's
  // course (accommodation_hotel_ids) — index 0 is the recommended pairing.
  const accommodationCandidates = useMemo(() => {
    const ids = anchorCourse?.accommodation_hotel_ids;
    if (!ids?.length) return [];
    return ids
      .map((id) => accommodations.find((a) => a.id === id))
      .filter(Boolean);
  }, [anchorCourse, accommodations]);
  const recommendedAccommodation = accommodationCandidates[0] || null;

  // Day-trip packages (0 nights) don't include a hotel — staying over is
  // optional, and only happens once the golfer explicitly opts in.
  const isOptionalAccommodation = !(pkg?.nights > 0);
  const [accommodationOptIn, setAccommodationOptIn] = useState(false);
  const [optInNights, setOptInNights] = useState(1);
  const showAccommodation = pkg?.nights > 0 || accommodationOptIn;
  const effectiveNights = pkg?.nights > 0 ? pkg.nights : optInNights;

  const selectedAccommodation = showAccommodation
    ? accommodationOverride || recommendedAccommodation
    : null;

  const handleRemoveAccommodation = () => {
    setAccommodationOptIn(false);
    setAccommodationOverride(null);
  };

  // Rule: inbound date carries over to ferry + accommodation check-in; check-out
  // follows the effective night count from that same date.
  // Memoized on primitive values — addDays() otherwise returns a new Date
  // object every render, which would re-trigger the effect below forever.
  const checkInDate = inboundDate || null;
  const checkInTimestamp = checkInDate ? checkInDate.getTime() : null;
  const checkOutDate = useMemo(
    () =>
      checkInDate && effectiveNights > 0
        ? addDays(checkInDate, effectiveNights)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkInTimestamp, effectiveNights],
  );
  // Rule: check-in time is a fixed hotel policy, shown but never editable.
  const checkInTime = selectedAccommodation ? HOTEL_CHECK_IN_TIME : null;

  // Pricing plan #5, #6, #8: nightly total via the centralized engine.
  const nightDates = useMemo(() => {
    if (!checkInDate || !effectiveNights) return [];
    return Array.from({ length: effectiveNights }, (_, i) =>
      addDays(checkInDate, i),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInTimestamp, effectiveNights]);

  const accommodationBreakdown = useMemo(
    () =>
      selectedAccommodation
        ? calculateAccommodationTotal({
            accommodation: selectedAccommodation,
            nightDates,
            sharingType,
            numRooms,
          })
        : { nights: [], total: 0 },
    [selectedAccommodation, nightDates, sharingType, numRooms],
  );
  const accommodationTotal = accommodationBreakdown.total;

  const inboundPeriod = deriveTeePeriod(inboundTeeTime);

  // Flat per-vehicle rate. Free entirely when vehicle_free_for_one_round applies.
  const transportTotal = selectedTransport
    ? calculateTransportTotal({
        course: anchorCourse,
        vehicle: selectedTransport,
        totalGuests: players,
        numRounds,
        date: inboundDate,
        period: inboundPeriod,
      }).total
    : 0;

  // Price label for the land transport card.
  const estimateTransportOptionTotal = (transportOption) =>
    calculateTransportTotal({
      course: anchorCourse,
      vehicle: transportOption,
      totalGuests: players,
      numRounds,
      date: inboundDate,
      period: inboundPeriod,
    }).total;

  // Ferry price is a ONE-WAY fare — always charged twice (inbound + outbound).
  const totalFerryCost = selectedFerry
    ? calculateFerryTotal({ ferry: selectedFerry, totalGuests: players })
    : 0;

  // Keep the parent page's snapshot of these selections live, so Checkout
  // always reflects the latest choice.
  useEffect(() => {
    onSelectionChange({
      selectedFerry,
      selectedFerryTime,
      selectedFerryArrivalTime,
      selectedOutboundFerryTime,
      totalFerryCost,
      selectedTransport: vehicleFree ? null : selectedTransport,
      selectedTransportTime,
      selectedOutboundTransport: vehicleFree
        ? null
        : recommendedOutboundTransport,
      selectedOutboundTransportTime,
      vehicleFree,
      totalTransportCost: transportTotal,
      selectedAccommodation,
      accommodationTotal,
      sharingType,
      numRooms: selectedAccommodation ? numRooms : null,
      checkInDate,
      checkInTime,
      checkOutDate,
    });
  }, [
    selectedFerry,
    selectedFerryTime,
    selectedFerryArrivalTime,
    selectedOutboundFerryTime,
    totalFerryCost,
    selectedTransport,
    selectedTransportTime,
    recommendedOutboundTransport,
    selectedOutboundTransportTime,
    vehicleFree,
    transportTotal,
    selectedAccommodation,
    accommodationTotal,
    sharingType,
    numRooms,
    checkInTimestamp,
    checkInTime,
    checkOutDate,
  ]);
  if (loadingTravelOptions) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-6"
      >
        <div className="flex items-center justify-center gap-2 text-gray-400 py-12">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading recommended travel options...
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-6"
    >
      <div className="mb-5">
        <h2 className="text-[#0B2421] font-bold text-xl">
          Your Recommended Travel
        </h2>
        <p className="text-gray-400 text-sm mt-1 max-w-xl">
          Hand-picked ferry, land transport
          {selectedAccommodation ? " and accommodation" : ""} for your{" "}
          <span className="font-semibold text-gray-600">{pkg.title}</span>{" "}
          package ({players} player{players > 1 ? "s" : ""}) — or change any of
          them below.
        </p>
      </div>

      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {selectedFerry && (
            <TravelCategoryCard
              icon={Ship}
              title="Ferry"
              confirmMessage="Recommended Ferry Options as per your selection"
              selected={selectedFerry}
              recommendedId={recommendedFerry?.id}
              options={allFerryOptions}
              getName={(f) => f.title}
              getSubtitle={(f) => `${f.originport} ⇄ ${f.destination}`}
              metaLines={[
                {
                  icon: <Clock className="h-3 w-3 shrink-0" />,
                  text: `Inbound: ${fmtDate(inboundDate)}, Departs ${sgTime(selectedFerryTime)}, Arrives ${idTime(selectedFerryArrivalTime)}.`,
                },
                {
                  icon: <Clock className="h-3 w-3 shrink-0" />,
                  text: `Outbound: ${fmtDate(outboundDate)}, Departs ${idTime(selectedOutboundFerryTime)}, Arrives ${sgTime(selectedOutboundFerryArrivalTime)}.`,
                },
                {
                  icon: <Users className="h-3 w-3 shrink-0" />,
                  text: `${playersLabel}, ${numRounds} Round${numRounds > 1 ? "s" : ""}`,
                },
              ]}
              getPriceLabel={(f) =>
                `${formatSGD(calculateFerryTotal({ ferry: f, totalGuests: players }))} Total`
              }
              note={TIME_ZONE_NOTE}
              onChange={handleFerryChange}
              timeEditors={[
                {
                  label: "Inbound departure time (SG Time)",
                  value: selectedFerryTime,
                  options: inboundFerryTimeOptions,
                  onChange: setFerryInboundTimeOverride,
                },
                {
                  label: "Outbound (return) departure time (ID Time)",
                  value: selectedOutboundFerryTime,
                  options: outboundFerryTimeOptions,
                  onChange: setFerryOutboundTimeOverride,
                },
              ]}
            />
          )}
          {selectedTransport && !vehicleFree && (
            <TravelCategoryCard
              icon={Car}
              title="Land Transport"
              confirmMessage="Recommended Ground Transport as per your selection"
              selected={selectedTransport}
              recommendedId={recommendedTransport?.id}
              options={[]}
              allowChange={false}
              getName={(t) => (t.type ? `${t.title} · ${t.type}` : t.title)}
              getSubtitle={() =>
                `Pickup: ${pickupTerminal || "—"} ferry terminal`
              }
              metaLines={[
                {
                  icon: <Clock className="h-3 w-3 shrink-0" />,
                  text: `Inbound Pickup: ${fmtDate(inboundDate)}, ${selectedTransportTime ? "~" : ""}${idTime(selectedTransportTime)}`,
                },
                {
                  icon: <Clock className="h-3 w-3 shrink-0" />,
                  text: `Outbound Pickup: ${fmtDate(outboundDate)}, ${selectedOutboundTransportTime ? "~" : ""}${idTime(selectedOutboundTransportTime)}`,
                },
                {
                  icon: <Users className="h-3 w-3 shrink-0" />,
                  text: `${playersLabel}, ${numRounds} Round${numRounds > 1 ? "s" : ""}`,
                },
              ]}
              note={TIME_ZONE_NOTE}
              getPriceLabel={(t) =>
                `${formatSGD(estimateTransportOptionTotal(t))} Total`
              }
            />
          )}
          {vehicleFree && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-emerald-50 rounded-2xl border border-emerald-100 overflow-hidden flex flex-col items-center justify-center text-center p-6"
            >
              <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <Car className="h-5 w-5 text-emerald-600" />
              </div>
              <h3 className="text-[#0B2421] font-bold text-base mb-1">
                Vehicle Included
              </h3>
              <p className="text-emerald-700 text-xs max-w-[220px]">
                This course includes the vehicle free for a single round — no
                need to select land transport.
              </p>
            </motion.div>
          )}
          {/* Day-trip package — no accommodation option, just a notice. */}
          {isOptionalAccommodation && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 overflow-hidden flex items-center justify-center text-center p-6"
            >
              <p className="text-[#0B2421] font-bold text-sm max-w-[240px]">
                This package is a day trip with one round of golf and does not
                include accommodation.
              </p>
            </motion.div>
          )}

          {selectedAccommodation && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
            >
              <div className="relative h-36 overflow-hidden">
                <img
                  src={selectedAccommodation.image}
                  alt={selectedAccommodation.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-5">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  {isOptionalAccommodation
                    ? "Your optional overnight stay"
                    : "Recommended Accommodation as per your selection"}
                </p>
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 shrink-0">
                    <Sparkles className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                      {selectedAccommodation.id === recommendedAccommodation?.id
                        ? "Recommended"
                        : "Selected"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAccommodationDialogOpen(true)}
                    className="text-xs font-semibold text-[#0B2421] bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-3 py-1 transition-colors shrink-0"
                  >
                    Change
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
                    <Bed className="h-4 w-4 text-[#D4AF37]" />
                  </div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Accommodation
                  </p>
                </div>
                <h3 className="text-[#0B2421] font-bold text-base mb-1">
                  {selectedAccommodation.title}
                </h3>
                <div className="flex items-center gap-3 mt-2 mb-3 flex-wrap">
                  <div className="rounded-lg bg-[#0B2421] text-white px-3 py-1.5 text-center leading-tight">
                    <p className="text-[11px] font-bold">Single Room</p>
                    <p className="text-[11px] font-bold">
                      {selectedAccommodation.bed_type || "King Bed"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-[11px] font-semibold text-gray-500 leading-tight">
                      No. of
                      <br />
                      Rooms
                    </p>
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setNumRoomsOverride(numRooms - 1)}
                        disabled={numRooms <= minRooms}
                        className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#0B2421] disabled:opacity-30"
                        aria-label="Fewer rooms"
                      >
                        −
                      </button>
                      <span className="w-5 text-center text-sm font-bold text-[#0B2421]">
                        {numRooms}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNumRoomsOverride(numRooms + 1)}
                        disabled={numRooms >= maxRooms}
                        className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#0B2421] disabled:opacity-30"
                        aria-label="More rooms"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {isOptionalAccommodation && (
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg">
                      <button
                        type="button"
                        onClick={() =>
                          setOptInNights((n) => Math.max(1, n - 1))
                        }
                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-[#0B2421] text-xs"
                        aria-label="Fewer nights"
                      >
                        −
                      </button>
                      <span className="text-xs font-semibold text-[#0B2421] w-3 text-center">
                        {effectiveNights}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setOptInNights((n) => Math.min(14, n + 1))
                        }
                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-[#0B2421] text-xs"
                        aria-label="More nights"
                      >
                        +
                      </button>
                      <span className="text-xs text-gray-500 pr-1.5">
                        night{effectiveNights > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
                {checkInDate && checkOutDate && (
                  <div className="mb-3 space-y-1">
                    <p className="text-xs font-medium text-gray-500 flex items-start gap-1.5">
                      <Clock className="h-3 w-3 shrink-0 mt-0.5" />
                      Check In: {fmtDate(checkInDate)}, after{" "}
                      {idTime(checkInTime)}
                    </p>
                    <p className="text-xs font-medium text-gray-500 flex items-start gap-1.5">
                      <Clock className="h-3 w-3 shrink-0 mt-0.5" />
                      Check Out: {fmtDate(checkOutDate)}, by{" "}
                      {idTime(HOTEL_CHECK_OUT_TIME)}
                    </p>
                  </div>
                )}
                <p className="text-[#0B2421] font-bold text-lg">
                  {formatSGD(accommodationTotal)} Total
                </p>
                <p className="text-[11px] text-gray-400 italic mt-2 pt-2 border-t border-gray-100 leading-snug">
                  {TIME_ZONE_NOTE}
                </p>
                {isOptionalAccommodation && (
                  <button
                    type="button"
                    onClick={handleRemoveAccommodation}
                    className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 mt-3"
                  >
                    Don't need to stay after all
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {(selectedAccommodation || accommodationDialogOpen) && (
        <AccommodationOptionsDialog
          open={accommodationDialogOpen}
          onOpenChange={setAccommodationDialogOpen}
          options={allAccommodationOptions}
          selectedId={selectedAccommodation?.id}
          nights={effectiveNights}
          nightDates={nightDates}
          sharingType={sharingType}
          numRooms={numRooms}
          onSelect={(a) => {
            setAccommodationOverride(a);
            setAccommodationDialogOpen(false);
          }}
        />
      )}

      {/* Footer CTA — navigates to the checkout page */}
      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl mt-6">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-gray-400 text-sm hidden sm:block">
            Use the "Change" button to select a different option
          </p>
          <button
            onClick={onContinue}
            className="ml-auto shrink-0 flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-base bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] shadow-md hover:shadow-lg transition-all"
          >
            Proceed to Booking <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}